import { roundToPrecision } from '../chart-precision/chart-precision';

interface HumenFrindlyTicksResult {
  ticks: number[];
  step: number;
}

const DEFAULT_STEPS = [1, 2, 3, 5, 10, 25];
const DEFAULT_RANGE_LENGTH = 1;

/**
 * `maxTicks` bounds the number of tick *intervals*, so up to
 * `maxTicks + 1` tick values can be returned.
 * @param cover - Round the tick range outward so ticks enclose the input
 * range. Use when the chart's scale domain adopts the tick bounds, so the
 * first/last gridline lands exactly on the plot edges. Defaults to
 * `false` (ticks stay inside the range).
 */
export const humanFriendlyTicks = (
  range: [number, number] | number[],
  maxTicks: number,
  cover = false
): HumenFrindlyTicksResult => {
  const [rangeMin, rangeMax] = [Math.min(...range), Math.max(...range)];
  const rangeLength = rangeMax - rangeMin || DEFAULT_RANGE_LENGTH;

  //calculate min/max bounds to get human readable ticks
  const max = DEFAULT_STEPS[DEFAULT_STEPS.length - 1] * maxTicks;
  const min = DEFAULT_STEPS[0] * maxTicks;

  //calc scale required to get range into bounds
  let scale = 1;

  if (rangeLength > max) {
    while (rangeLength / scale > max) {
      scale *= 10;
    }
  } else if (rangeLength < min) {
    while (rangeLength / scale < min) {
      scale /= 10;
    }
  }

  // rescale range to be in min/max bounds
  const rescaledRange = [rangeMin, rangeMax].map(x => x / scale);

  const trimRange = (step: number): [number, number] =>
    cover
      ? [
          Math.floor(rescaledRange[0] / step) * step,
          Math.ceil(rescaledRange[1] / step) * step,
        ]
      : [
          Math.ceil(rescaledRange[0] / step) * step,
          Math.floor(rescaledRange[1] / step) * step,
        ];

  // find a step which meets the maxTicks criteria; covering outward can
  // overshoot by up to two intervals, so fall back to the coarsest step
  const step =
    DEFAULT_STEPS.find(step => {
      const trimmedRange = trimRange(step);
      return (trimmedRange[1] - trimmedRange[0]) / step <= maxTicks;
    }) ?? DEFAULT_STEPS[DEFAULT_STEPS.length - 1];

  // calc ticks using step calculated above
  const trimmedRange = trimRange(step);

  const tickCount = Math.round((trimmedRange[1] - trimmedRange[0]) / step) + 1;
  const ticks = Array.from({ length: tickCount }, (_, i) => {
    const tick = (trimmedRange[0] + step * i) * scale;

    if (scale < 1) {
      return roundToPrecision(tick, scale);
    }

    return tick;
  });

  return {
    step: scale * step,
    ticks,
  };
};
